// RPT1 (docs/build-briefs/RPT1-owner-reports-slice-1.md): the pure, DOM-free
// helpers behind the Reports screen (ui/src/reports.ts), kept apart so a
// plain node:test file can import them (see test/ui-report-format.test.ts):
// the date range presets, the percent-change wording, and the money cells.
//
// Everything is calendar arithmetic on YYYY-MM-DD labels, anchored at UTC
// noon so a daylight saving change can never shift a day. The tenant's own
// "today" comes from the server (GET .../reports/context); this module never
// reads the browser clock for a business date.

export type RangePreset =
  | "today"
  | "yesterday"
  | "last7"
  | "last7days"
  | "last30"
  | "last90"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth"
  | "custom";

export const RANGE_PRESETS: ReadonlyArray<{ id: RangePreset; label: string }> = [
  { id: "yesterday", label: "Yesterday" },
  { id: "last7", label: "Last 7 days" },
  { id: "thisWeek", label: "This week" },
  { id: "lastWeek", label: "Last week" },
  { id: "thisMonth", label: "This month" },
  { id: "custom", label: "Custom" },
];

/** INV33 (DEC-290): the inventory report's chips, in the approved mockup's order. */
export const USAGE_WASTE_PRESETS: ReadonlyArray<{ id: RangePreset; label: string }> = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "thisWeek", label: "This week" },
  { id: "lastWeek", label: "Last week" },
  { id: "thisMonth", label: "This month" },
  { id: "lastMonth", label: "Last month" },
  { id: "custom", label: "Custom dates" },
];

/** INV35-A1 (DEC-292): the Count variance chips. Counts happen about weekly, so
 * the default is the last 30 days. These rolling chips end TODAY, as the sales
 * reports' "Last 7 days" does since UX102. */
export const COUNT_VARIANCE_PRESETS: ReadonlyArray<{ id: RangePreset; label: string }> = [
  { id: "last7days", label: "Last 7 days" },
  { id: "last30", label: "Last 30 days" },
  { id: "thisMonth", label: "This month" },
  { id: "lastMonth", label: "Last month" },
  { id: "last90", label: "Last 90 days" },
  { id: "custom", label: "Custom dates" },
];

export function addDays(dateIso: string, days: number): string {
  const [y, m, d] = dateIso.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days, 12)).toISOString().slice(0, 10);
}

/** 0 = Sunday ... 6 = Saturday. */
function weekday(dateIso: string): number {
  const [y, m, d] = dateIso.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay();
}

/** Whole days from `fromIso` to `toIso`, inclusive count (same day is 1). */
export function inclusiveDayCount(fromIso: string, toIso: string): number {
  const [fy, fm, fd] = fromIso.split("-").map(Number) as [number, number, number];
  const [ty, tm, td] = toIso.split("-").map(Number) as [number, number, number];
  return Math.round((Date.UTC(ty, tm - 1, td, 12) - Date.UTC(fy, fm - 1, fd, 12)) / 86_400_000) + 1;
}

/** The Monday on or before the date (a week starts on Monday, owner question 3). */
function mondayOnOrBefore(dateIso: string): string {
  const back = (weekday(dateIso) + 6) % 7;
  return addDays(dateIso, -back);
}

/**
 * The from and to dates a preset means, given the tenant's current business
 * date. "Last 7 days" is the seven business days ending TODAY (UX102: it used
 * to end yesterday, so a ticket closed this morning showed "No sales" with
 * today missing). "Yesterday" is the one preset that stops before today.
 * "This week" and "This month" may include today, so they never reach into
 * the future. Returns null for "custom", which the caller reads from its own
 * two date inputs.
 */
export function rangeForPreset(
  preset: RangePreset,
  today: string,
): { from: string; to: string } | null {
  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "last7days":
      return { from: addDays(today, -6), to: today };
    case "last30":
      return { from: addDays(today, -29), to: today };
    case "last90":
      return { from: addDays(today, -89), to: today };
    case "lastMonth": {
      const lastDay = addDays(`${today.slice(0, 8)}01`, -1);
      return { from: `${lastDay.slice(0, 8)}01`, to: lastDay };
    }
    case "yesterday":
      return { from: addDays(today, -1), to: addDays(today, -1) };
    case "last7":
      return { from: addDays(today, -6), to: today };
    case "thisWeek":
      return { from: mondayOnOrBefore(today), to: today };
    case "lastWeek": {
      const thisMonday = mondayOnOrBefore(today);
      return { from: addDays(thisMonday, -7), to: addDays(thisMonday, -1) };
    }
    case "thisMonth":
      return { from: `${today.slice(0, 8)}01`, to: today };
    case "custom":
      return null;
  }
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** "Thu Sep 24". */
export function dayLabel(dateIso: string): string {
  const [, m, d] = dateIso.split("-").map(Number) as [number, number, number];
  return `${WEEKDAYS[weekday(dateIso)]} ${MONTHS[m - 1]} ${d}`;
}

/** "Thu". */
export function weekdayLabel(dateIso: string): string {
  return WEEKDAYS[weekday(dateIso)] ?? "";
}

/** "9/24". */
export function monthDayLabel(dateIso: string): string {
  const [, m, d] = dateIso.split("-").map(Number) as [number, number, number];
  return `${m}/${d}`;
}

export interface PercentChange {
  /** "4.8%" */
  text: string;
  direction: "up" | "down" | "flat";
}

/**
 * (current - prior) / prior, one decimal. Null when the prior is zero (an
 * infinite change is omitted, not shown), so a new location never reports
 * "infinity percent".
 */
export function percentChange(current: number, prior: number): PercentChange | null {
  if (prior === 0) {
    return null;
  }
  const raw = ((current - prior) / Math.abs(prior)) * 100;
  const rounded = Math.round(Math.abs(raw) * 10) / 10;
  const text = `${rounded.toFixed(1)}%`;
  if (rounded === 0) {
    return { text, direction: "flat" };
  }
  return { text, direction: raw > 0 ? "up" : "down" };
}

/** A fraction (0.1040) as "10.4%". */
export function formatShare(fraction: number): string {
  return `${(Math.round(fraction * 1000) / 10).toFixed(1)}%`;
}

/** Whole-dollar-and-cents text with a thousands separator: 325550 -> "$3,255.50". */
export function formatMoney(cents: number): string {
  const negative = cents < 0;
  const magnitude = Math.abs(cents);
  const dollars = Math.trunc(magnitude / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const remainder = String(magnitude % 100).padStart(2, "0");
  return `${negative ? "-" : ""}$${dollars}.${remainder}`;
}

/** A count with a thousands separator: 1059 -> "1,059". */
export function formatCount(value: number): string {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export interface MoneyCell {
  text: string;
  /** "zero" renders as a quiet dash; "reduction" as a muted negative. */
  tone: "plain" | "zero" | "reduction";
}

/**
 * A money column that takes something off sales (discounts, comps, refunds):
 * shown as a negative, and as a dash when it is zero.
 */
export function reductionCell(cents: number): MoneyCell {
  if (cents === 0) {
    return { text: "-", tone: "zero" };
  }
  return { text: formatMoney(-Math.abs(cents)), tone: "reduction" };
}

/** An ordinary money column; a zero tip day is a dash. */
export function amountCell(cents: number, dashWhenZero: boolean): MoneyCell {
  if (cents === 0 && dashWhenZero) {
    return { text: "-", tone: "zero" };
  }
  return { text: formatMoney(cents), tone: "plain" };
}

/** An average ticket: a dash, never 0 or NaN, when there were no tickets. */
export function avgTicketText(cents: number | null): string {
  return cents === null ? "-" : formatMoney(cents);
}

/** Every date from `from` to `to`, inclusive. */
export function datesBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) {
    out.push(d);
  }
  return out;
}

/** "04:00" as "4:00 AM"; midnight reads "midnight". */
export function cutoffText(cutoffLocal: string): string {
  const [hh, mm] = cutoffLocal.split(":").map(Number) as [number, number];
  if (hh === 0 && mm === 0) return "midnight";
  const hour12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${hour12}:${String(mm).padStart(2, "0")} ${hh < 12 ? "AM" : "PM"}`;
}
